'use client';

import { AvatarIconSlot, AvatarImageSlot, AvatarSpinnerSlot } from '@magicschool/spellbook/components/Avatar/slots';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import type { SpellbookNameOverrideProps } from '@magicschool/spellbook/lib/slots';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as AvatarPrimitive from 'radix-ui/avatar';
// import { useState } from 'react';

export type AvatarProps = React.ComponentProps<typeof AvatarPrimitive.Root> &
  React.ComponentProps<typeof AvatarPrimitive.Image> &
  VariantProps<typeof avatarVariants> &
  SpellbookNameOverrideProps & {
    /** Configuration for component default text content, labels, placeholders, and screen reader announcements. */
    text?: {
      /** Accessible label for the avatar. If not provided, the `role` of the image/icon will be set to `presentation`. */
      label?: string;
    };
    /** Whether or not the Avatar is loading. */
    isLoading?: boolean;
    'data-testid'?: string;
  };

export const avatarVariants = cva(
  ['relative', 'flex', 'shrink-0', 'transition-colors', 'overflow-hidden', 'items-center', 'justify-center', 'rounded-full'],
  {
    variants: {
      /** The visual appearance of the fallback avatar (one option for now). */
      variant: {
        default: 'bg-primary-muted-background text-primary-icon [&_svg]:text-primary-icon',
      },
      /** The size of the avatar. */
      size: {
        sm: 'size-control-h-sm min-w-control-h-sm min-h-control-h-sm',
        md: 'size-control-h-md min-w-control-h-md min-h-control-h-md',
        lg: 'size-control-h-lg min-w-control-h-lg min-h-control-h-lg',
        xl: 'size-16 min-w-16 min-h-16', // Avatar only
      },
      /** Whether or not the avatar is expecting an image (has a `src` prop) */
      hasImage: {
        true: '',
        false: '',
      },
    },
  },
);

const AvatarBase: React.FC<AvatarProps> = ({
  children,
  'data-spellbook-name': spellbookName,
  className,
  size = 'md',
  variant = 'default',
  text = {},
  onLoadingStatusChange: providedOnLoadingStatusChange,
  isLoading = false,
  /**
   * @TODO [SPE-11] Simplify Test ID Propagation
   * https://linear.app/magicschool/issue/SPE-11/simplify-test-id-propagation
   */
  'data-testid': dataTestId,
  ...props
}) => {
  const { label } = text;
  /**
   * @TODO [SPE-9] Custom loading state, Next Image support for Avatar
   * https://linear.app/magicschool/issue/SPE-9/custom-loading-state-next-image-support-for-avatar
   *
   * Leaving the coded up and commented out controlled loading state logic here for now.
   */

  // const [internalLoading, setInternalLoading] = useState<boolean>(isLoading);

  const handleImageLoadingStatusChange = (status: 'loading' | 'loaded' | 'error' | 'idle') => {
    // if (status === 'loaded' || status === 'error') {
    //   setInternalLoading(false);
    // } else {
    //   setInternalLoading(true);
    // }

    providedOnLoadingStatusChange?.(status);
  };

  const iconSize = size === 'xl' ? 'lg' : size;

  const { slots } = useSlots(children, {
    image: {
      Component: AvatarImageSlot,
      props: { isLoading, size: iconSize, onLoadingStatusChange: handleImageLoadingStatusChange, label, ...props },
    },
    icon: {
      Component: !isLoading ? AvatarIconSlot : AvatarSpinnerSlot,
      props: { size: iconSize, variant, label, alt: props?.alt, 'aria-label': props?.['aria-label'] },
    },
  });

  return (
    <AvatarPrimitive.Root
      data-spellbook-name="avatar"
      {...(spellbookName && { 'data-spellbook-name': spellbookName })}
      data-testid={dataTestId}
      className={cn(avatarVariants({ size, variant, hasImage: Boolean(props.src), className }))}
    >
      {slots.image}
      <AvatarPrimitive.Fallback asChild>
        <>{slots.icon}</>
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  );
};

/**
 * Displays a user's profile image with automatic fallback to an icon or spinner when the image is unavailable or loading.
 *
 * ### When to use
 * - To represent a user, contact, or entity with a profile image
 * - When you need graceful fallback behavior (icon when no image, spinner while loading)
 * - Inside `Caterpillar` for stacked user lists, or alongside user names in tables and cards
 *
 * ### When NOT to use
 * - For decorative or illustrative images: use a standard `<img>` or `Icon` instead
 * - For tool or brand logos that don't need fallback behavior: use `Icon` or `<img>` directly
 *
 * ### API
 * - `size`: Avatar dimensions (`'sm'`, `'md'`, `'lg'`, `'xl'`)
 * - `src`: Image URL — when provided, renders the image; when missing or failed, shows the fallback icon
 * - `text.label`: Accessible label for the avatar. If omitted and no `alt` is provided, the avatar is treated as decorative (`aria-hidden`)
 * - `isLoading`: When `true`, displays a spinner in place of the image or fallback icon
 *
 * ### Composition (slots)
 * - `<Avatar.Image>`: The profile image. Rendered automatically when `src` is provided — typically no need to include explicitly. Pass image props (`src`, `alt`) directly to `Avatar` instead.
 * - `<Avatar.Icon>`: Fallback icon shown when no image is available. Rendered automatically as a user icon — only include to customize.
 * - `<Avatar.Spinner>`: Loading indicator shown when `isLoading` is `true`. Rendered automatically — only include to customize.
 *
 * ### Visual states
 * - **Default (no src)**: Shows fallback icon (user silhouette) on a muted primary background
 * - **With image**: Shows the profile image, cropped to fill the circular container
 * - **Loading**: Shows a spinner in place of the image or icon
 * - **Image error**: Falls back to the icon automatically via Radix's built-in fallback behavior
 *
 * ### Accessibility
 * - When `text.label` is provided, it is used as `aria-label` on the image or `role="img"` on the fallback icon
 * - When no label or `alt` is provided, the avatar is marked as decorative (`aria-hidden="true"`, `role="presentation"`)
 * - Prefer `text.label` over `alt` when the avatar has no image — `alt` only applies to the `<img>` element
 */
export const Avatar = Object.assign(AvatarBase, {
  Image: AvatarImageSlot,
  Spinner: AvatarSpinnerSlot,
  Icon: AvatarIconSlot,
});
